import React, { useEffect, useMemo, useState } from 'react';
import styles from './index.module.scss';
import { Col, Row } from 'antd';
import Manage from './components/Manage';
import Customize from './components/Customize';
import {
  useCurrent,
  Wrapper,
  getRealUrl,
  useBreadcrumbRoutes,
  Tabs,
  Button,
  Modal,
  message,
} from '@maxtropy/components';
import { getDefaultLogo } from '../../api/file';
import { useHasPermission } from '../../utils/hooks';
import { PermissionsType } from '../../common/permissionsConst';
import SyncModal from './components/SyncModal';
import { cpSync, cpSyncProgress } from '../../api/customer';

const { TabPane } = Tabs;

enum TabKey {
  MANAGE = 'manage',
  CUSTOMIZE = 'customize',
}

const Organization: React.FC = () => {
  const [activeKey, setActiveKey] = useState<string | undefined>();
  const [logo, setLogo] = useState<string>();
  const [visible, setVisible] = useState<boolean>(false);
  const [syncLoading, setSyncLoading] = useState<boolean>(false);
  const user = useCurrent();
  const isRootNode = user?.customer?.mcid === user?.tenant?.mcid;

  const allowUpdateCustomer = useHasPermission(PermissionsType.PUPDATECUSTOMER);
  const allowTenantPersonalized = useHasPermission(PermissionsType.PTENANTPERSONALIZED);
  const hasSyncExternalSystem = useHasPermission(PermissionsType.B_SYNCHRONIZE_EXTERNAL_SYSTEM_ORGANIZATIONS);
  const hasViewProgressSync = useHasPermission(PermissionsType.B_VIEW_PROGRESS_SYNC);

  const breadcrumbRoutes = useBreadcrumbRoutes();

  const tabContent: Record<TabKey, React.ReactElement> = {
    manage: <Manage user={user} isRootNode={isRootNode} />,
    customize: <Customize />,
  };

  const tabs = useMemo(
    () => [
      {
        name: '组织管理',
        key: TabKey.MANAGE,
        enable: allowUpdateCustomer,
      },
      {
        name: '组织名自定义',
        key: TabKey.CUSTOMIZE,
        enable: allowTenantPersonalized,
      },
    ],
    [allowTenantPersonalized, allowUpdateCustomer]
  );

  useEffect(() => {
    if (user) {
      if (user.customer?.logoUrl) {
        setLogo(getRealUrl(user.customer.logoUrl));
      } else {
        getDefaultLogo().then(data => {
          setLogo(getRealUrl(data.key));
        });
      }
    }
  }, [user]);

  useEffect(() => {
    setActiveKey(tabs.find(item => item.enable)?.key ?? undefined);
  }, [tabs]);

  const syncCP = () => {
    cpSyncProgress().then(res => {
      // if (res.progress && res.progress < 100) {
      //   Modal.info({
      //     title: '数据同步进行中，请同步完成后再点击！',
      //   });
      //   return;
      // }

      setSyncLoading(true);
      cpSync()
        .then(() => {
          message.success('开始同步...');
        })
        .finally(() => setSyncLoading(false));
    });
  };
  const syncCpProgress = () => {
    cpSyncProgress().then(res => {
      if (!res.progress) {
        Modal.info({
          title: '暂无同步信息',
        });
        return;
      }
      setVisible(true);
    });
  };

  return (
    <Wrapper routes={breadcrumbRoutes?.routes} className={styles.wrapper}>
      <div className={styles.topBar}>
        <img style={{ height: 64 }} src={logo} alt="logo" />
        <div className={styles.info}>
          <div className={styles.code}>{user?.tenant?.tenantCode}</div>
          <div className={styles.company}>
            {user?.customer?.fullName + `${isRootNode ? `-${user?.tenant?.name}` : ''}`}
          </div>
        </div>
        {(hasSyncExternalSystem || hasViewProgressSync) && (
          <div className={styles.actions}>
            {hasSyncExternalSystem && (
              <Button type="primary" loading={syncLoading} onClick={syncCP}>
                同步外部系统组织
              </Button>
            )}
            {hasViewProgressSync && (
              <Button type="primary" onClick={syncCpProgress}>
                同步进度
              </Button>
            )}
          </div>
        )}
      </div>
      <div className={styles.background} style={{ minHeight: 'calc(100vh - 230px)' }}>
        <Tabs activeKey={activeKey} onChange={v => setActiveKey(v)}>
          {tabs.map(item =>
            item.enable ? (
              <TabPane tab={item.name} key={item.key}>
                {tabContent[item.key]}
              </TabPane>
            ) : (
              <div key={item.key} />
            )
          )}
        </Tabs>
      </div>
      {visible && <SyncModal onCancel={() => setVisible(false)} />}
    </Wrapper>
  );
};

export default Organization;
